<template>
  <div class="voucher">
    <div class="flex gap-5 justify-between items-end mb-6 mdmax:flex-col">
      <div class="flex w-full gap-5 mdmax:w-full mdmax:flex-col">
        <div class="w-full max-w-lg mdmax:w-full">
          <label
            class="text-base text-neutral-700 font-medium mb-1 inline-block"
            >Pencarian</label
          >
          <div class="relative">
            <TextField
              type="text"
              :withIcon="true"
              placeholder="Cari voucher..."
              v-model="search"
              @enter="onSearch"
            />
            <div class="absolute left-3 top-1/2 transform -translate-y-1/2">
              <Search />
            </div>
          </div>
        </div>
      </div>

      <div class="mdmax:w-full">
        <button
          @click="$router.push('/voucher/create')"
          class="px-4 py-2 min-w-[140px] bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          + Add Voucher
        </button>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr
              class="bg-neutral-50 text-neutral-500 text-sm font-semibold border-b"
            >
              <th class="p-4">Nama Voucher</th>
              <th class="p-4">Kode</th>
              <th class="p-4">Periode</th>
              <th class="p-4">Diskon</th>
              <th class="p-4">Kuota</th>
              <th class="p-4">Status</th>
              <th class="p-4">Action</th>
            </tr>
          </thead>

          <tbody class="text-sm text-neutral-700">
            <tr
              v-for="voucher in paginatedItems"
              :key="voucher.id"
              class="border-b hover:bg-neutral-50"
            >
              <td class="p-4 font-medium">{{ voucher.name }}</td>
              <td class="p-4">{{ voucher.voucher_code }}</td>
              <td class="p-4">
                {{ formatDate(voucher.start_date) }} -
                {{ formatDate(voucher.end_date) }}
              </td>
              <td class="p-4">{{ voucher.discount_percentage }}%</td>
              <td class="p-4">
                {{ voucher.available_quota }} / {{ voucher.quota }}
              </td>
              <td class="p-4">
                <span
                  class="px-2 py-1 rounded text-xs font-medium"
                  :class="statusClass(voucher.status)"
                >
                  {{ voucher.status }}
                </span>
              </td>
              <td class="p-4">
                <div class="flex gap-3">
                  <button
                    class="text-neutral-500 hover:text-primary-500"
                    @click="goToEdit(voucher.id)"
                  >
                    <Pencil width="18" height="18" />
                  </button>
                  <button
                    class="text-neutral-500 hover:text-red-500"
                    @click="openDeleteModal(voucher)"
                  >
                    <Trash width="18" height="18" />
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="paginatedItems.length === 0">
              <td colspan="7" class="p-4 text-center text-neutral-500">
                Data tidak ditemukan
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="text-sm text-neutral-500 mt-2" v-if="filteredItems.length">
      Menampilkan {{ startItem }} - {{ endItem }} dari
      {{ filteredItems.length }}
    </div>

    <div class="mt-8 flex justify-end" v-if="filteredItems.length">
      <Pagination
        :currentPage="page"
        :totalCount="filteredItems.length"
        :pageSize="limit"
        @onPageChange="changePage"
      />
    </div>

    <Modal
      :modalVisible="deleteModalVisible"
      @close="closeDeleteModal"
      width="400px"
    >
      <div slot="modal-content" class="text-center">
        <h3 class="text-lg font-bold text-neutral-800 mb-2">Hapus Voucher</h3>
        <p class="text-neutral-600 mb-6">
          Apakah Anda yakin ingin menghapus voucher
          <strong>{{ voucherToDelete?.name }}</strong
          >?
        </p>
        <div class="flex justify-center gap-3">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
          >
            Batal
          </button>
          <button
            @click="confirmDelete"
            class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
          >
            Hapus
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import dayjs from "dayjs"
import { mapActions, mapGetters } from "vuex"

export default {
  name: "Voucher",
  components: {
    TextField: () => import("@/components/Forms/TextField"),
    Pagination: () => import("@/components/Pagination/Pagination"),
    Search: () => import("@/components/Icons/Search"),
    Pencil: () => import("@/components/Icons/Pencil"),
    Trash: () => import("@/components/Icons/Trash"),
    Modal: () => import("@/components/Modal/Modal")
  },
  data() {
    return {
      search: "",
      page: 1,
      limit: 10,
      deleteModalVisible: false,
      voucherToDelete: null
    }
  },
  computed: {
    ...mapGetters("voucher", ["voucherList"]),
    filteredItems() {
      if (!this.search) return this.voucherList
      return this.voucherList.filter(
        (v) =>
          v.name.toLowerCase().includes(this.search.toLowerCase()) ||
          v.voucher_code.toLowerCase().includes(this.search.toLowerCase())
      )
    },
    paginatedItems() {
      const start = (this.page - 1) * this.limit
      return this.filteredItems.slice(start, start + this.limit)
    },
    startItem() {
      return (this.page - 1) * this.limit + 1
    },
    endItem() {
      return Math.min(this.page * this.limit, this.filteredItems.length)
    }
  },
  methods: {
    ...mapActions("voucher", ["getVouchers", "deleteVoucher"]),
    ...mapActions("common", ["showLoading", "hideLoading", "showExactToast"]),

    formatDate(date) {
      return date ? dayjs(date).format("DD MMM YYYY") : "-"
    },
    statusClass(status) {
      return status === "Active"
        ? "bg-green-100 text-green-700"
        : "bg-gray-100 text-gray-700"
    },
    changePage(val) {
      this.page = val
    },
    onSearch() {
      this.page = 1
    },
    async fetchVouchers() {
      try {
        await this.getVouchers({ params: {} })
      } catch (err) {
        console.error("Gagal fetch vouchers:", err)
      }
    },
    goToEdit(id) {
      this.$router.push(`/voucher/${id}`)
    },
    openDeleteModal(voucher) {
      this.voucherToDelete = voucher
      this.deleteModalVisible = true
    },
    closeDeleteModal() {
      this.deleteModalVisible = false
      this.voucherToDelete = null
    },
    async confirmDelete() {
      if (!this.voucherToDelete) return

      this.showLoading()
      try {
        await this.deleteVoucher({ id: this.voucherToDelete.id })
        this.showExactToast({
          message: "Promo berhasil dihapus",
          type: "success"
        })
        this.fetchVouchers()
      } catch (error) {
        this.showExactToast({
          message: error.response?.data?.message || "Gagal menghapus promo",
          type: "error"
        })
      } finally {
        this.hideLoading()
        this.closeDeleteModal()
      }
    }
  },
  created() {
    this.fetchVouchers()
  }
}
</script>

<style scoped>
.voucher ::v-deep .vs__search {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
}
</style>
